import React, { useState, useEffect } from 'react';
import API_URL from './config';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'UTC', label: 'UTC' },
];

function AdminSettings({ token }) {
  const [timezone, setTimezone] = useState('America/New_York');
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderSameDay, setReminderSameDay] = useState(true);
  const [welcomeEmailEnabled, setWelcomeEmailEnabled] = useState(true);
  const [welcomeEmailSubject, setWelcomeEmailSubject] = useState('Welcome to The Bridge Sports Complex!');
  const [welcomeEmailBody, setWelcomeEmailBody] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);
  const [savingWelcomeEmail, setSavingWelcomeEmail] = useState(false);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [pendingInfo, setPendingInfo] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings`);
      const data = await res.json();
      if (data.timezone) {
        setTimezone(data.timezone);
      }
      if (data.reminder_enabled !== undefined) {
        setReminderEnabled(data.reminder_enabled === 'true' || data.reminder_enabled === true);
      }
      if (data.reminder_same_day !== undefined) {
        setReminderSameDay(data.reminder_same_day === 'true' || data.reminder_same_day === true);
      }
      // Welcome email settings
      if (data.welcome_email_enabled !== undefined) {
        setWelcomeEmailEnabled(data.welcome_email_enabled === 'true' || data.welcome_email_enabled === true);
      }
      if (data.welcome_email_subject) {
        setWelcomeEmailSubject(data.welcome_email_subject);
      }
      if (data.welcome_email_body) {
        setWelcomeEmailBody(data.welcome_email_body);
      }
      if (data.contact_email) {
        setContactEmail(data.contact_email);
      }
      if (data.contact_phone) {
        setContactPhone(data.contact_phone);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTimezone = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/settings/timezone`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: timezone })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }
      setMessage('Timezone saved successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReminderSettings = async () => {
    setSavingReminders(true);
    setMessage('');
    setError('');
    try {
      // Save reminder_enabled
      let res = await fetch(`${API_URL}/api/settings/reminder_enabled`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: reminderEnabled.toString() })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save reminder_enabled');
      }

      // Save reminder_same_day
      res = await fetch(`${API_URL}/api/settings/reminder_same_day`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: reminderSameDay.toString() })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save reminder_same_day');
      }

      setMessage('Reminder settings saved successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingReminders(false);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    setMessage('');
    setError('');
    try {
      // First check what's pending
      const checkRes = await fetch(`${API_URL}/api/admin/pending-reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const checkData = await checkRes.json();
      setPendingInfo(checkData);
      
      // Then trigger reminders
      const res = await fetch(`${API_URL}/api/admin/trigger-reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to trigger reminders');
      }
      const resultMsg = data.result?.sent > 0 
        ? `Sent ${data.result.sent} reminder(s) to ${data.result.users} user(s)` 
        : 'No pending reminders to send';
      setMessage(`✅ ${resultMsg}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setTriggeringReminders(false);
    }
  };

  const handleSaveWelcomeEmail = async () => {
    setSavingWelcomeEmail(true);
    setMessage('');
    setError('');
    try {
      // Save all welcome email settings
      const settings = [
        { key: 'welcome_email_enabled', value: welcomeEmailEnabled.toString() },
        { key: 'welcome_email_subject', value: welcomeEmailSubject },
        { key: 'welcome_email_body', value: welcomeEmailBody },
        { key: 'contact_email', value: contactEmail },
        { key: 'contact_phone', value: contactPhone }
      ];

      for (const setting of settings) {
        const res = await fetch(`${API_URL}/api/settings/${setting.key}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ value: setting.value })
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || `Failed to save ${setting.key}`);
        }
      }

      setMessage('Welcome email settings saved successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingWelcomeEmail(false);
    }
  };

  // Get current time in selected timezone
  const getCurrentTimeInTimezone = () => {
    try {
      return new Date().toLocaleString('en-US', { 
        timeZone: timezone,
        dateStyle: 'medium',
        timeStyle: 'medium'
      });
    } catch {
      return 'Invalid timezone';
    }
  };

  if (loading) {
    return <div className="admin-section"><p>Loading settings...</p></div>;
  }

  return (
    <div className="admin-section">
      <h3>⚙️ App Settings</h3>
      
      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}

      <div className="settings-group">
        <h4>Timezone</h4>
        <p className="settings-description">
          This timezone is used for determining available time slots and displaying booking times.
        </p>
        
        <div className="settings-row">
          <label htmlFor="timezone">Facility Timezone:</label>
          <select 
            id="timezone"
            value={timezone} 
            onChange={(e) => setTimezone(e.target.value)}
            className="timezone-select"
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <p className="timezone-preview">
          Current time in {TIMEZONES.find(tz => tz.value === timezone)?.label || timezone}: <strong>{getCurrentTimeInTimezone()}</strong>
        </p>

        <button 
          onClick={handleSaveTimezone} 
          disabled={saving}
          className="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Timezone'}
        </button>
      </div>

      <div className="settings-group" style={{ marginTop: '32px' }}>
        <h4>📧 Email Reminders</h4>
        <p className="settings-description">
          Automatic reminders are sent approximately 24 hours before bookings. 
          The system runs hourly to catch all bookings starting in the next hour window.
        </p>
        
        <div className="settings-row" style={{ marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={reminderEnabled}
              onChange={(e) => setReminderEnabled(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>Enable automatic reminder emails</span>
          </label>
        </div>

        <div className="settings-row" style={{ marginTop: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={reminderSameDay}
              onChange={(e) => setReminderSameDay(e.target.checked)}
              disabled={!reminderEnabled}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ color: !reminderEnabled ? '#999' : 'inherit' }}>
              Send immediate reminder when same-day bookings are approved
            </span>
          </label>
        </div>

        <p className="settings-description" style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
          💡 Same-day bookings approved after the 24h reminder window will receive an immediate confirmation email instead.
        </p>

        <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button 
            onClick={handleSaveReminderSettings} 
            disabled={savingReminders}
            className="save-settings-btn"
          >
            {savingReminders ? 'Saving...' : 'Save Reminder Settings'}
          </button>
          <button 
            onClick={handleTriggerReminders} 
            disabled={triggeringReminders || !reminderEnabled}
            className="save-settings-btn"
            style={{ background: '#059669' }}
            title={!reminderEnabled ? 'Enable reminders first' : 'Manually run the reminder check now'}
          >
            {triggeringReminders ? 'Running...' : '▶ Run Reminders Now'}
          </button>
        </div>

        {pendingInfo && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '6px', fontSize: '13px' }}>
            <strong>Debug Info:</strong>
            <div style={{ marginTop: '8px' }}>
              <div>Today: {pendingInfo.currentDate?.today}</div>
              <div>Tomorrow: {pendingInfo.currentDate?.tomorrow}</div>
              <div>Approved bookings (today/tomorrow): {pendingInfo.count}</div>
            </div>
            {pendingInfo.bookings?.length > 0 && (
              <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#e2e8f0' }}>
                      <th style={{ padding: '4px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '4px', textAlign: 'left' }}>Time</th>
                      <th style={{ padding: '4px', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '4px', textAlign: 'center' }}>Reminder Sent</th>
                      <th style={{ padding: '4px', textAlign: 'center' }}>Email Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInfo.bookings.map(b => (
                      <tr key={b.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '4px' }}>{b.booking_date}</td>
                        <td style={{ padding: '4px' }}>{b.time_slot}</td>
                        <td style={{ padding: '4px' }}>{b.email}</td>
                        <td style={{ padding: '4px', textAlign: 'center' }}>{b.reminder_sent ? '✅' : '❌'}</td>
                        <td style={{ padding: '4px', textAlign: 'center' }}>{b.email_reminders_enabled === false ? '❌' : '✅'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="settings-group" style={{ marginTop: '32px' }}>
        <h4>👋 Welcome Email</h4>
        <p className="settings-description">
          Configure the welcome email that is automatically sent to new users when they register an account.
        </p>
        
        <div className="settings-row" style={{ marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={welcomeEmailEnabled}
              onChange={(e) => setWelcomeEmailEnabled(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>Enable welcome email for new registrations</span>
          </label>
        </div>

        <div className="settings-row" style={{ marginTop: '16px' }}>
          <label htmlFor="contactEmail" style={{ fontWeight: '500', marginBottom: '4px', display: 'block' }}>
            Contact Email:
          </label>
          <input 
            type="email"
            id="contactEmail"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="contact@example.com"
            style={{ 
              width: '100%', 
              maxWidth: '400px', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div className="settings-row" style={{ marginTop: '12px' }}>
          <label htmlFor="contactPhone" style={{ fontWeight: '500', marginBottom: '4px', display: 'block' }}>
            Contact Phone:
          </label>
          <input 
            type="tel"
            id="contactPhone"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="(555) 123-4567"
            style={{ 
              width: '100%', 
              maxWidth: '400px', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div className="settings-row" style={{ marginTop: '12px' }}>
          <label htmlFor="welcomeEmailSubject" style={{ fontWeight: '500', marginBottom: '4px', display: 'block' }}>
            Email Subject:
          </label>
          <input 
            type="text"
            id="welcomeEmailSubject"
            value={welcomeEmailSubject}
            onChange={(e) => setWelcomeEmailSubject(e.target.value)}
            placeholder="Welcome to Our Facility!"
            style={{ 
              width: '100%', 
              maxWidth: '400px', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div className="settings-row" style={{ marginTop: '12px' }}>
          <label htmlFor="welcomeEmailBody" style={{ fontWeight: '500', marginBottom: '4px', display: 'block' }}>
            Email Body:
          </label>
          <textarea 
            id="welcomeEmailBody"
            value={welcomeEmailBody}
            onChange={(e) => setWelcomeEmailBody(e.target.value)}
            placeholder="Thank you for registering! We look forward to seeing you at our facility. Please feel free to contact us with any questions."
            rows={5}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
          <p className="settings-description" style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
            💡 Use {'{first_name}'} to include the user's first name in the email.
          </p>
        </div>

        <button 
          onClick={handleSaveWelcomeEmail} 
          disabled={savingWelcomeEmail}
          className="save-settings-btn"
          style={{ marginTop: '16px' }}
        >
          {savingWelcomeEmail ? 'Saving...' : 'Save Welcome Email Settings'}
        </button>
      </div>
    </div>
  );
}

export default AdminSettings;
